/** Internal type. DO NOT USE DIRECTLY. */
import { gql } from "@apollo/client"

import * as Types from "../../../shared/types/__generated__/graphql"
import {
  FloorChildrenFragmentDoc,
  FloorFragmentDoc
} from "../../floors/graphql/fragments.generated"
import { ProductPositionFragmentDoc } from "../../product-positions/graphql/fragments.generated"
import {
  RoomChildrenFragmentDoc,
  RoomFragmentDoc
} from "../../rooms/graphql/fragments.generated"
import { UserFragmentDoc } from "../../team-members/graphql/fragments.generated"
import { ProjectPhaseFragmentDoc } from "./project-phases/fragments.generated"
import { SpecialPriceGroupFragmentDoc } from "./special-price-groups/fragments.generated"

export type Incremental<T> =
  | T
  | {
      [P in keyof T]?: P extends " $fragmentName" | "__typename" ? T[P] : never
    }

export type UserPermissionFragment = {
  __typename: "UserPermission"
  id: string
  resourceType: string
  resourceId: string
  role: string
  permissions?: {
    __typename: "ProjectPermissions"
    view_project: boolean
    edit_project: boolean
    edit_position_price: boolean
    edit_position_price_group: boolean
    create_special_price_group: boolean
    delete_project: boolean
    invite_members: boolean
  } | null
}

export type ProjectIdFragment = { __typename: "Project"; id: string }

export type ProjectPreviewFragment = {
  __typename: "Project"
  id: string
  title: string
  phase?: {
    __typename: "ProjectPhase"
    id: string
    value: string
    label: string
  } | null
}

export type ProjectFragment = {
  __typename: "Project"
  id: string
  title: string
  description?: string | null
  position: number
  priceList?: string | null
  inflationSurcharge?: number | null
  createdAt: string
  updatedAt: string
  lastChanged?: string | null
  hasClarificationRequest?: boolean | null
  ui?: { __typename: "ProjectUI"; color?: string | null } | null
  lastChangedBy?: {
    __typename: "User"
    id: string
    name?: string | null
    email: string
    createdAt: string
    updatedAt: string
  } | null
  phase?: {
    __typename: "ProjectPhase"
    id: string
    value: string
    label: string
  } | null
  team?: { __typename: "ProjectTeamID"; id: string } | null
}

export type ProjectSpecialPriceGroupsFragment = {
  __typename: "Project"
  specialPriceGroups?: Array<{
    __typename: "SpecialPriceGroup"
    id: string
    name: string
    parentPriceGroup?: string | null
    productType: string
  }> | null
}

export type ProjectChildrenFragment = {
  __typename: "Project"
  children?: Array<
    | {
        __typename: "Floor"
        id: string
        label?: string | null
        position: number
        createdAt: string
        updatedAt: string
        remark?: string | null
        hasClarificationRequest?: boolean | null
        children?: Array<{
          __typename: "Room"
          id: string
          label?: string | null
          number: string
          area?: number | null
          volume?: number | null
          utilization?: string | null
          height?: number | null
          personsNumber?: number | null
          airVolumePerArea?: number | null
          airVolumePerPerson?: number | null
          remark?: string | null
          sup?: number | null
          oda?: number | null
          eta?: number | null
          eha?: number | null
          tra?: number | null
          rca?: number | null
          ida?: number | null
          sec?: number | null
          soundPressureLevel?: number | null
          position: number
          createdAt: string
          updatedAt: string
          hasClarificationRequest?: boolean | null
          children?: Array<{
            __typename: "ProductPosition"
            id: string
            label?: string | null
            variantKey: string
            configurationKey?: string | null
            remark?: string | null
            quantity: number
            position: number
            displayPosition?: string | null
            isAlternative?: boolean | null
            price?: number | null
            priceGroup?: string | null
            hasClarificationRequest?: boolean | null
            references?: Array<{
              __typename: "ProductPositionInstallationReference"
              id: string
              title?: string | null
              permalink: string
              position: number
            }> | null
            addons?: Array<{
              __typename: "ProductPositionAddon"
              id: string
              variantKey: string
              parentProductType: string
            }> | null
            dimensioning?: {
              __typename: "ExternalDimensioningResult"
              transactionId: string
              environmentalVars?: Array<any> | null
              variantKeyData?: any
              dllVersion?: string | null
              language?: string | null
              technicalData?: Array<
                | {
                    __typename: "ExternalDimensioningTechnicalDataDllVersion"
                    dllVersion: string
                  }
                | {
                    __typename: "ExternalDimensioningTechnicalDataTable"
                    header?: Array<string> | null
                    content?: any
                    summary?: string | null
                    type?: string | null
                  }
              > | null
              technicalInput?: Array<{
                __typename: "ExternalDimensioningTechnicalInput"
                name: string
                label?: string | null
                unit?: string | null
                value?: any
                userSelected: boolean
              }> | null
              supplementaryText?: Array<{
                __typename: "ExternalDimensioningSupplementaryText"
                text: string
              }> | null
              transferData?: {
                __typename: "ExternalDimensioningTransferData"
                origin?: string | null
              } | null
            } | null
          }> | null
        }> | null
      }
    | {
        __typename: "ProductPosition"
        id: string
        label?: string | null
        variantKey: string
        configurationKey?: string | null
        remark?: string | null
        quantity: number
        position: number
        displayPosition?: string | null
        isAlternative?: boolean | null
        price?: number | null
        priceGroup?: string | null
        hasClarificationRequest?: boolean | null
        references?: Array<{
          __typename: "ProductPositionInstallationReference"
          id: string
          title?: string | null
          permalink: string
          position: number
        }> | null
        addons?: Array<{
          __typename: "ProductPositionAddon"
          id: string
          variantKey: string
          parentProductType: string
        }> | null
        dimensioning?: {
          __typename: "ExternalDimensioningResult"
          transactionId: string
          environmentalVars?: Array<any> | null
          variantKeyData?: any
          dllVersion?: string | null
          language?: string | null
          technicalData?: Array<
            | {
                __typename: "ExternalDimensioningTechnicalDataDllVersion"
                dllVersion: string
              }
            | {
                __typename: "ExternalDimensioningTechnicalDataTable"
                header?: Array<string> | null
                content?: any
                summary?: string | null
                type?: string | null
              }
          > | null
          technicalInput?: Array<{
            __typename: "ExternalDimensioningTechnicalInput"
            name: string
            label?: string | null
            unit?: string | null
            value?: any
            userSelected: boolean
          }> | null
          supplementaryText?: Array<{
            __typename: "ExternalDimensioningSupplementaryText"
            text: string
          }> | null
          transferData?: {
            __typename: "ExternalDimensioningTransferData"
            origin?: string | null
          } | null
        } | null
      }
    | {
        __typename: "Room"
        id: string
        label?: string | null
        number: string
        area?: number | null
        volume?: number | null
        utilization?: string | null
        height?: number | null
        personsNumber?: number | null
        airVolumePerArea?: number | null
        airVolumePerPerson?: number | null
        remark?: string | null
        sup?: number | null
        oda?: number | null
        eta?: number | null
        eha?: number | null
        tra?: number | null
        rca?: number | null
        ida?: number | null
        sec?: number | null
        soundPressureLevel?: number | null
        position: number
        createdAt: string
        updatedAt: string
        hasClarificationRequest?: boolean | null
        children?: Array<{
          __typename: "ProductPosition"
          id: string
          label?: string | null
          variantKey: string
          configurationKey?: string | null
          remark?: string | null
          quantity: number
          position: number
          displayPosition?: string | null
          isAlternative?: boolean | null
          price?: number | null
          priceGroup?: string | null
          hasClarificationRequest?: boolean | null
          references?: Array<{
            __typename: "ProductPositionInstallationReference"
            id: string
            title?: string | null
            permalink: string
            position: number
          }> | null
          addons?: Array<{
            __typename: "ProductPositionAddon"
            id: string
            variantKey: string
            parentProductType: string
          }> | null
          dimensioning?: {
            __typename: "ExternalDimensioningResult"
            transactionId: string
            environmentalVars?: Array<any> | null
            variantKeyData?: any
            dllVersion?: string | null
            language?: string | null
            technicalData?: Array<
              | {
                  __typename: "ExternalDimensioningTechnicalDataDllVersion"
                  dllVersion: string
                }
              | {
                  __typename: "ExternalDimensioningTechnicalDataTable"
                  header?: Array<string> | null
                  content?: any
                  summary?: string | null
                  type?: string | null
                }
            > | null
            technicalInput?: Array<{
              __typename: "ExternalDimensioningTechnicalInput"
              name: string
              label?: string | null
              unit?: string | null
              value?: any
              userSelected: boolean
            }> | null
            supplementaryText?: Array<{
              __typename: "ExternalDimensioningSupplementaryText"
              text: string
            }> | null
            transferData?: {
              __typename: "ExternalDimensioningTransferData"
              origin?: string | null
            } | null
          } | null
        }> | null
      }
  > | null
}

export type ProjectCompleteFragment = {
  __typename: "Project"
  id: string
  title: string
  description?: string | null
  position: number
  priceList?: string | null
  inflationSurcharge?: number | null
  createdAt: string
  updatedAt: string
  lastChanged?: string | null
  hasClarificationRequest?: boolean | null
  ui?: { __typename: "ProjectUI"; color?: string | null } | null
  lastChangedBy?: {
    __typename: "User"
    id: string
    name?: string | null
    email: string
    createdAt: string
    updatedAt: string
  } | null
  phase?: {
    __typename: "ProjectPhase"
    id: string
    value: string
    label: string
  } | null
  team?: { __typename: "ProjectTeamID"; id: string } | null
  children?: Array<
    | {
        __typename: "Floor"
        id: string
        label?: string | null
        position: number
        createdAt: string
        updatedAt: string
        remark?: string | null
        hasClarificationRequest?: boolean | null
        children?: Array<{
          __typename: "Room"
          id: string
          label?: string | null
          number: string
          area?: number | null
          volume?: number | null
          utilization?: string | null
          height?: number | null
          personsNumber?: number | null
          airVolumePerArea?: number | null
          airVolumePerPerson?: number | null
          remark?: string | null
          sup?: number | null
          oda?: number | null
          eta?: number | null
          eha?: number | null
          tra?: number | null
          rca?: number | null
          ida?: number | null
          sec?: number | null
          soundPressureLevel?: number | null
          position: number
          createdAt: string
          updatedAt: string
          hasClarificationRequest?: boolean | null
          children?: Array<{
            __typename: "ProductPosition"
            id: string
            label?: string | null
            variantKey: string
            configurationKey?: string | null
            remark?: string | null
            quantity: number
            position: number
            displayPosition?: string | null
            isAlternative?: boolean | null
            price?: number | null
            priceGroup?: string | null
            hasClarificationRequest?: boolean | null
            references?: Array<{
              __typename: "ProductPositionInstallationReference"
              id: string
              title?: string | null
              permalink: string
              position: number
            }> | null
            addons?: Array<{
              __typename: "ProductPositionAddon"
              id: string
              variantKey: string
              parentProductType: string
            }> | null
            dimensioning?: {
              __typename: "ExternalDimensioningResult"
              transactionId: string
              environmentalVars?: Array<any> | null
              variantKeyData?: any
              dllVersion?: string | null
              language?: string | null
              technicalData?: Array<
                | {
                    __typename: "ExternalDimensioningTechnicalDataDllVersion"
                    dllVersion: string
                  }
                | {
                    __typename: "ExternalDimensioningTechnicalDataTable"
                    header?: Array<string> | null
                    content?: any
                    summary?: string | null
                    type?: string | null
                  }
              > | null
              technicalInput?: Array<{
                __typename: "ExternalDimensioningTechnicalInput"
                name: string
                label?: string | null
                unit?: string | null
                value?: any
                userSelected: boolean
              }> | null
              supplementaryText?: Array<{
                __typename: "ExternalDimensioningSupplementaryText"
                text: string
              }> | null
              transferData?: {
                __typename: "ExternalDimensioningTransferData"
                origin?: string | null
              } | null
            } | null
          }> | null
        }> | null
      }
    | {
        __typename: "ProductPosition"
        id: string
        label?: string | null
        variantKey: string
        configurationKey?: string | null
        remark?: string | null
        quantity: number
        position: number
        displayPosition?: string | null
        isAlternative?: boolean | null
        price?: number | null
        priceGroup?: string | null
        hasClarificationRequest?: boolean | null
        references?: Array<{
          __typename: "ProductPositionInstallationReference"
          id: string
          title?: string | null
          permalink: string
          position: number
        }> | null
        addons?: Array<{
          __typename: "ProductPositionAddon"
          id: string
          variantKey: string
          parentProductType: string
        }> | null
        dimensioning?: {
          __typename: "ExternalDimensioningResult"
          transactionId: string
          environmentalVars?: Array<any> | null
          variantKeyData?: any
          dllVersion?: string | null
          language?: string | null
          technicalData?: Array<
            | {
                __typename: "ExternalDimensioningTechnicalDataDllVersion"
                dllVersion: string
              }
            | {
                __typename: "ExternalDimensioningTechnicalDataTable"
                header?: Array<string> | null
                content?: any
                summary?: string | null
                type?: string | null
              }
          > | null
          technicalInput?: Array<{
            __typename: "ExternalDimensioningTechnicalInput"
            name: string
            label?: string | null
            unit?: string | null
            value?: any
            userSelected: boolean
          }> | null
          supplementaryText?: Array<{
            __typename: "ExternalDimensioningSupplementaryText"
            text: string
          }> | null
          transferData?: {
            __typename: "ExternalDimensioningTransferData"
            origin?: string | null
          } | null
        } | null
      }
    | {
        __typename: "Room"
        id: string
        label?: string | null
        number: string
        area?: number | null
        volume?: number | null
        utilization?: string | null
        height?: number | null
        personsNumber?: number | null
        airVolumePerArea?: number | null
        airVolumePerPerson?: number | null
        remark?: string | null
        sup?: number | null
        oda?: number | null
        eta?: number | null
        eha?: number | null
        tra?: number | null
        rca?: number | null
        ida?: number | null
        sec?: number | null
        soundPressureLevel?: number | null
        position: number
        createdAt: string
        updatedAt: string
        hasClarificationRequest?: boolean | null
        children?: Array<{
          __typename: "ProductPosition"
          id: string
          label?: string | null
          variantKey: string
          configurationKey?: string | null
          remark?: string | null
          quantity: number
          position: number
          displayPosition?: string | null
          isAlternative?: boolean | null
          price?: number | null
          priceGroup?: string | null
          hasClarificationRequest?: boolean | null
          references?: Array<{
            __typename: "ProductPositionInstallationReference"
            id: string
            title?: string | null
            permalink: string
            position: number
          }> | null
          addons?: Array<{
            __typename: "ProductPositionAddon"
            id: string
            variantKey: string
            parentProductType: string
          }> | null
          dimensioning?: {
            __typename: "ExternalDimensioningResult"
            transactionId: string
            environmentalVars?: Array<any> | null
            variantKeyData?: any
            dllVersion?: string | null
            language?: string | null
            technicalData?: Array<
              | {
                  __typename: "ExternalDimensioningTechnicalDataDllVersion"
                  dllVersion: string
                }
              | {
                  __typename: "ExternalDimensioningTechnicalDataTable"
                  header?: Array<string> | null
                  content?: any
                  summary?: string | null
                  type?: string | null
                }
            > | null
            technicalInput?: Array<{
              __typename: "ExternalDimensioningTechnicalInput"
              name: string
              label?: string | null
              unit?: string | null
              value?: any
              userSelected: boolean
            }> | null
            supplementaryText?: Array<{
              __typename: "ExternalDimensioningSupplementaryText"
              text: string
            }> | null
            transferData?: {
              __typename: "ExternalDimensioningTransferData"
              origin?: string | null
            } | null
          } | null
        }> | null
      }
  > | null
}

export const UserPermissionFragmentDoc = gql`
  fragment UserPermission on UserPermission {
    id
    resourceType
    resourceId
    role
    permissions {
      view_project
      edit_project
      edit_position_price
      edit_position_price_group
      create_special_price_group
      delete_project
      invite_members
    }
  }
`
export const ProjectIdFragmentDoc = gql`
  fragment ProjectID on Project {
    id
  }
`
export const ProjectPreviewFragmentDoc = gql`
  fragment ProjectPreview on Project {
    id
    title
    phase @type(name: "ProjectPhase") {
      ...ProjectPhase
    }
  }
  ${ProjectPhaseFragmentDoc}
`
export const ProjectSpecialPriceGroupsFragmentDoc = gql`
  fragment ProjectSpecialPriceGroups on Project {
    specialPriceGroups @type(name: "SpecialPriceGroup") {
      ...SpecialPriceGroup
    }
  }
  ${SpecialPriceGroupFragmentDoc}
`
export const ProjectFragmentDoc = gql`
  fragment Project on Project {
    id
    title
    description
    position
    priceList
    inflationSurcharge
    ui {
      color
    }
    createdAt
    updatedAt
    lastChanged
    lastChangedBy @type(name: "User") {
      ...User
    }
    phase @type(name: "ProjectPhase") {
      ...ProjectPhase
    }
    team @type(name: "ProjectTeamID") {
      id
    }
    hasClarificationRequest
  }
  ${UserFragmentDoc}
  ${ProjectPhaseFragmentDoc}
`
export const ProjectChildrenFragmentDoc = gql`
  fragment ProjectChildren on Project {
    children {
      ... on Floor {
        ...Floor
        ...FloorChildren
      }
      ... on Room {
        ...Room
        ...RoomChildren
      }
      ... on ProductPosition {
        ...ProductPosition
      }
    }
  }
  ${FloorFragmentDoc}
  ${FloorChildrenFragmentDoc}
  ${RoomFragmentDoc}
  ${RoomChildrenFragmentDoc}
  ${ProductPositionFragmentDoc}
`
export const ProjectCompleteFragmentDoc = gql`
  fragment ProjectComplete on Project {
    ...Project
    ...ProjectChildren
  }
  ${ProjectFragmentDoc}
  ${ProjectChildrenFragmentDoc}
`
